import React, { useEffect, useState } from "react";
import { Card, Spin, Collapse } from "antd";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import axios from "axios";
import { useAuth0 } from "../../../react-auth0-spa";
import { useAppContext } from "../../../context";
import config from "../../../api_config.json";
import { useIsMobile } from "../../../hooks/useIsMobile";

// Fix for Leaflet marker icon issue in React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const centerItalyPosition = [41.9028, 12.4964];

export default function OrgMap({ orgId }) {
  const [position, setPosition] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [displayAddress, setDisplayAddress] = useState("");
  const [orgName, setOrgName] = useState("");
  const { getTokenSilently } = useAuth0();
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";
  const isMobile = useIsMobile();
  const mapHeight = isMobile ? 300 : 700;

  const ORG_COORDS_QUERY = `
    query FindOrgCoord($where: Org_coordWhereInput!) {
      org_coords(
        where: $where
      ) {
        edges {
          node {
            objectId
            latitude
            longitude
            org_pointer {
              objectId
              nome
              indirizzo
              citta
              cap
              provincia
              nazione
            }
          }
        }
      }
    }`;

  useEffect(() => {
    if (!orgId) {
      setPosition(centerItalyPosition);
      setError(
        "ID organizzazione non disponibile. Mostrata posizione predefinita."
      );
      setLoading(false);
      return;
    }

    const fetchOrgCoords = async () => {
      try {
        setLoading(true);
        const token = await getTokenSilently();
        const graphqlUrl = `${config.baseUrl[env]}/graphql?raw=true`;

        const variables = {
          where: {
            org_pointer: {
              have: {
                objectId: {
                  equalTo: orgId,
                },
              },
            },
          },
        };

        const response = await axios({
          method: "post",
          url: graphqlUrl,
          data: {
            query: ORG_COORDS_QUERY,
            variables: variables,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        // Per docs, ?raw=true means response only has one .data level
        if (
          response.data?.org_coords?.edges &&
          response.data.org_coords.edges.length > 0
        ) {
          const coordData = response.data.org_coords.edges[0].node;
          const orgData = coordData.org_pointer;

          // Extract coordinates
          const lat = parseFloat(coordData.latitude);
          const lng = parseFloat(coordData.longitude);

          if (!isNaN(lat) && !isNaN(lng)) {
            setPosition([lat, lng]);
            setOrgName(orgData.nome || "");

            // Format address for display from org data
            const addressParts = [
              orgData.indirizzo,
              orgData.cap,
              orgData.citta,
              orgData.provincia ? `(${orgData.provincia})` : "",
              orgData.nazione || "Italia",
            ].filter(Boolean);
            
            const address = addressParts.join(", ");
            setDisplayAddress(address);
            setLoading(false);
            return;
          }
        }

        // Default position if no valid coordinates found
        setPosition(centerItalyPosition);
        setError("Coordinate non trovate. Mostrata posizione predefinita.");
        setLoading(false);
      } catch (err) {
        console.error("Error fetching org coordinates:", err);
        setPosition(centerItalyPosition);
        setError(
          "Errore nel caricamento delle coordinate. Mostrata posizione predefinita."
        );
        setLoading(false);
      }
    };

    fetchOrgCoords();
  }, [orgId, getTokenSilently, env, ORG_COORDS_QUERY]);

  if (loading) {
    const loadingContent = (
      <div
        style={{
          height: mapHeight,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Spin tip="Caricamento mappa..." />
      </div>
    );

    if (isMobile) {
      return (
        <Collapse defaultActiveKey={[]} className="org-map-collapse">
          <Collapse.Panel header="Mappa" key="map">
            {loadingContent}
          </Collapse.Panel>
        </Collapse>
      );
    }

    return <Card title="Mappa">{loadingContent}</Card>;
  }

  const mapContent = position && (
    <MapContainer
      center={position}
      zoom={6}
      style={{ height: mapHeight, width: "100%" }}
      scrollWheelZoom
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution="© OpenStreetMap contributors"
      />
      <Marker position={position}>
        <Popup>
          <div>
            {orgName && (
              <>
                <strong>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(orgName)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {orgName}
                  </a>
                </strong>
                <br />
                <br />
              </>
            )}
            {displayAddress && (
              <>
                <strong>Indirizzo:</strong><br />
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(displayAddress)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {displayAddress}
                </a>
                <br />
                <br />
              </>
            )}
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${position[0]},${position[1]}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Apri coordinate in Google Maps
            </a>
          </div>
        </Popup>
      </Marker>
    </MapContainer>
  );

  if (isMobile) {
    return (
      <Collapse defaultActiveKey={[]} className="org-map-collapse">
        <Collapse.Panel
          header={
            <span>
              Mappa
              {error && <span style={{ color: "red", marginLeft: 8, fontSize: 12 }}>{error}</span>}
            </span>
          }
          key="map"
        >
          {mapContent}
        </Collapse.Panel>
      </Collapse>
    );
  }

  return (
    <Card
      title="Mappa"
      extra={error && <span style={{ color: "red" }}>{error}</span>}
    >
      {mapContent}
    </Card>
  );
}
